import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';
import posthog from 'posthog-js';

import { AppContext } from '../contexts/AppContext';
import { FeatureRequest, FeatureComment } from '../services/FeatureRequestService';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FeatureRequestList from '../components/features/FeatureRequestList';
import NewFeatureRequestDialog from '../components/features/NewFeatureRequestDialog';
import FeatureCommentsDialog from '../components/features/FeatureCommentsDialog';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Add from '@mui/icons-material/Add';

const FeatureRequests: React.FC = observer(() => {
  const { userService, featureRequestService } = useContext(AppContext);
  const intl = useIntl();

  const [comments, setComments] = useState<{[key: string]: FeatureComment[]}>({});
  const [commentCounts, setCommentCounts] = useState<{[key: string]: number}>({});
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: ''
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info"
  });
  
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message: string, severity: "success" | "error" | "warning" | "info") => {
    setSnackbar({ open: true, message, severity });
  };
  
  // Fetch feature requests and comment counts
  useEffect(() => {
    const loadData = async () => {
      try {
        await featureRequestService.loadFeatureRequests(userService.user?.id);

        // Load comment counts for all requests
        const requestIds = featureRequestService.featureRequests
          .map(req => req.id)
          .filter(id => id !== undefined) as string[];

        if (requestIds.length > 0) {
          const counts = await featureRequestService.getCommentCountsForRequests(requestIds);
          setCommentCounts(counts);
        }

        posthog.capture('feature_requests_viewed');
      } catch (error) {
        showSnackbar(
          intl.formatMessage({ id: "featureRequests.loadError" }),
          'error'
        );
      }
    };

    loadData();
  }, [featureRequestService, userService.user?.id, intl]);

  const handleCreateRequest = async () => {
    if (!userService.user) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.loginRequired" }),
        'warning'
      );
      return;
    }

    if (!newRequest.title || !newRequest.description) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.fillAllFields" }),
        'warning'
      );
      return;
    }

    try {
      const request = await featureRequestService.createFeatureRequest({
        title: newRequest.title,
        description: newRequest.description,
        userId: userService.user.id || '',
        userName: userService.user.displayName || intl.formatMessage({ id: "common.anonymous" }),
        status: 'new',
        tags: [],
      });

      featureRequestService.addNewFeatureRequest(request);
      
      setNewRequestOpen(false);
      setNewRequest({ title: '', description: '' });
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.createSuccess" }),
        'success'
      );
    } catch (error) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.createError" }),
        'error'
      );
    }
  };

  const handleVote = async (request: FeatureRequest) => {
    if (!userService.user) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.loginToVote" }),
        'warning'
      );
      return;
    }

    if (!request.id) return;

    try {
      await featureRequestService.voteForFeature(request.id, userService.user.id || '');
    } catch (error) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.voteError" }),
        'error'
      );
    }
  };

  const handleOpenComments = async (request: FeatureRequest) => {
    setSelectedRequest(request);
    setCommentDialogOpen(true);
    
    if (!request.id) return;
    
    try {
      const fetchedComments = await featureRequestService.getCommentsForFeature(
        request.id,
        userService.user?.id
      );
      setComments({
        ...comments,
        [request.id]: fetchedComments
      });
    } catch (error) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.commentsLoadError" }),
        'error'
      );
    }
  };

  const handleCommentSubmit = async () => {
    if (!userService.user || !selectedRequest || !selectedRequest.id || !newComment.trim()) {
      return;
    }

    try {
      const comment = await featureRequestService.addComment({
        featureRequestId: selectedRequest.id,
        userId: userService.user.id || '',
        userName: userService.user.displayName || intl.formatMessage({ id: "common.anonymous" }),
        comment: newComment,
      });

      setComments({
        ...comments,
        [selectedRequest.id]: [...(comments[selectedRequest.id] || []), comment]
      });

      // Update comment count (user's own unapproved comment counts locally)
      setCommentCounts({
        ...commentCounts,
        [selectedRequest.id]: (commentCounts[selectedRequest.id] || 0) + 1
      });

      setNewComment('');
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.commentAdded" }),
        'success'
      );
    } catch (error) {
      showSnackbar(
        intl.formatMessage({ id: "featureRequests.commentError" }),
        'error'
      );
    }
  };

  if (featureRequestService.isLoadingFeatureRequests) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box paddingX={4} paddingTop={4} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">
          <FormattedMessage id="featureRequests.title" />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setNewRequestOpen(true)}
          disabled={!userService.user}
        >
          <FormattedMessage id="featureRequests.newRequest" />
        </Button>
      </Box>

      <FeatureRequestList
        featureRequests={featureRequestService.featureRequests}
        commentCounts={commentCounts}
        onVote={handleVote}
        onOpenComments={handleOpenComments}
        user={userService.user}
      />

      <NewFeatureRequestDialog
        open={newRequestOpen}
        onClose={() => setNewRequestOpen(false)}
        newRequest={newRequest}
        setNewRequest={setNewRequest}
        onSubmit={handleCreateRequest}
      />

      <FeatureCommentsDialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        selectedRequest={selectedRequest}
        comments={selectedRequest ? comments[selectedRequest.id || ''] || [] : []}
        newComment={newComment}
        setNewComment={setNewComment}
        onSubmit={handleCommentSubmit}
        user={userService.user}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

export default FeatureRequests;